import {
  useModalContext,
  WSButton,
  WSCheckboxToggle,
  WSText
} from "@wingspanhq/fe-component-library";
import { MemberTaxDocumentSharePermission } from "@wingspanhq/payments/dist/interfaces";
import { AxiosError } from "axios";
import React, { useState } from "react";
import { SetupFlowRouteComponentProps } from "../../../components/SetupFlow/SetupFlow";
import { useWSMutation } from "../../../query/helpers";
import { useUserId } from "../../../query/hooks/helpers";
import { useMemberClientsQuery } from "../../../query/payments/queries";
import {
  getClientCompany,
  getClientEmail,
  getClientName
} from "../../../query/payments/selectors";
import { useEnterpriseQuery } from "../../../query/platform/queries";
import { QUERY_USERS_ACTIVITIES } from "../../../query/users/keys";
import { useActivities } from "../../../query/users/queries";
import { getPaymentsSetupProgress } from "../../../query/users/selectors";
import { WSQueries } from "../../../query/WSQuery";
import { paymentsService } from "../../../services/payments";
import { usersService } from "../../../services/users";
import { PaymentsSetupLayout } from "../../components/PaymentsSetupLayout/PaymentsSetupLayout";
import {
  TINVerificationModal,
  TIN_VERIFICATION_MODAL
} from "../../components/TINVerificationModal/TINVerificationModal";
import styles from "./styles.module.scss";

export const SetupConsent: React.FC<SetupFlowRouteComponentProps> = ({
  onBack,
  onNext
}) => {
  const userId = useUserId();
  const activityQuery = useActivities(userId);
  const [agreement, setAgreement] = useState(false);
  const [shareW9, setShareW9] = useState(false);
  const enterpriseQuery = useEnterpriseQuery();
  const memberClientsQuery = useMemberClientsQuery();
  const { openModal } = useModalContext();

  const [submit, submitMeta] = useWSMutation<any, AxiosError>(
    async () => {
      const memberClients = memberClientsQuery.data || [];

      await Promise.all(
        memberClients.map(memberClient =>
          paymentsService.memberClient.update(memberClient.memberClientId, {
            memberData: {
              shareTaxDocument: MemberTaxDocumentSharePermission.Allow
            }
          } as any)
        )
      );

      const paymentsSetupProgress = getPaymentsSetupProgress(
        activityQuery.data
      );

      await usersService.activity.update(userId, {
        events: {
          w9FormAcknowledged: new Date()
        }
      });

      openModal(TIN_VERIFICATION_MODAL, {
        onContinue: () => {
          usersService.activity.update(userId, {
            flows: {
              paymentsSetup: {
                ...paymentsSetupProgress,
                currentStep: 5
              }
            }
          });
          onNext();
        }
      });
    },
    {
      dependencies: [QUERY_USERS_ACTIVITIES]
    }
  );

  return (
    <PaymentsSetupLayout title="Certify your W-9 is accurate" onBack={onBack}>
      <TINVerificationModal />
      <WSQueries queries={{ memberClientsQuery }}>
        {({ memberClientsQuery: { data: memberClients } }) => {
          const employersNames: string[] = memberClients.map(
            memberClient =>
              getClientCompany(memberClient) ||
              getClientName(memberClient) ||
              getClientEmail(memberClient)
          );

          return (
            <>
              <WSText.Heading2 mb="M" data-testid="heading"></WSText.Heading2>

              <WSText mb="S">
                By using Wingspan’s services you agree that the information
                provided for Form W-9 is accurate and that you certify the
                provided information as follows:
              </WSText>
              <WSText mb="S">"I certify, under penalty of perjury that:</WSText>
              <WSText mb="S" as="ul" className={styles.bulletedList}>
                <li>
                  Taxpayer Identification Number or Social Security Number I
                  provided is correct;
                </li>
                <li>
                  I am not subject to backup withholding because: (a) I am
                  exempt from backup withholding, or (b) I have not been
                  notified by the Internal Revenue Service (IRS) that I am
                  subject to backup withholding as a result of a failure to
                  report all interest or dividends, or (c) the IRS has notified
                  me that I am no longer subject to backup withholding; and
                </li>
                <li>I am a U.S. citizen or other U.S. person; and</li>
                <li>
                  The FATCA code(s) entered (if any) indicating that I am exempt
                  from FATCA reporting is correct.
                </li>
              </WSText>
              <WSText>
                The Internal Revenue Service does not require your consent to
                any provision of this document other than the certifications
                required to avoid backup withholding."
              </WSText>

              <WSCheckboxToggle
                my="XL"
                name="agreement"
                label="By providing the information on this form, I hereby certify,
                to the best of my knowledge, that the information provided
                is complete and correct."
                value={agreement}
                onChange={value => {
                  setAgreement(value);
                }}
              />

              {employersNames.length > 0 && (
                <WSCheckboxToggle
                  my="XL"
                  name="shareW9"
                  label={`I consent to share my Form W-9 with ${employersNames.join(
                    ", "
                  )}`}
                  value={shareW9}
                  onChange={value => {
                    setShareW9(value);
                  }}
                />
              )}

              <WSButton
                disabled={
                  employersNames.length > 0
                    ? !agreement || !shareW9
                    : !agreement
                }
                mb="S"
                onClick={submit}
                fullWidth
                name="next"
                loading={submitMeta.isLoading}
              >
                Accept payments
                {enterpriseQuery.data
                  ? ` from ${enterpriseQuery.data.name}`
                  : ""}
              </WSButton>
            </>
          );
        }}
      </WSQueries>
    </PaymentsSetupLayout>
  );
};
